import React, { useState, useEffect, useCallback } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Trophy, Shield, ShoppingCart, BarChart3, Users, Zap, Download, Smartphone, Share2, X, BookOpen } from 'lucide-react';

const MatchCard = ({ match, isResult }) => {
  const [homeLogo, setHomeLogo] = useState(null);
  const [awayLogo, setAwayLogo] = useState(null);

  useEffect(() => {
    if (match.home_club_id) {
      fetch(`${API_URL}/api/clubs/${match.home_club_id}/logo`).then(r => r.json()).then(d => setHomeLogo(d.logo)).catch(() => {});
    }
    if (match.away_club_id) {
      fetch(`${API_URL}/api/clubs/${match.away_club_id}/logo`).then(r => r.json()).then(d => setAwayLogo(d.logo)).catch(() => {});
    }
  }, [match.home_club_id, match.away_club_id]);

  const ClubBadge = ({ name, logo, colors }) => {
    if (logo) return <img src={logo} alt={name} className="w-7 h-7 rounded object-cover" loading="lazy" />;
    return (
      <div className="w-7 h-7 rounded flex items-center justify-center text-[9px] font-bold text-white" style={{ background: colors?.primary || '#1a1a2e' }}>
        {name?.charAt(0) || '?'}
      </div>
    );
  };

  const dateStr = match.match_date ? new Date(match.match_date).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'TBD';

  return (
    <div className="px-4 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors">
      <div className="flex-1 flex items-center justify-end gap-2 min-w-0">
        <span className="text-sm font-medium text-white truncate">{match.home_club}</span>
        <ClubBadge name={match.home_club} logo={homeLogo} colors={match.home_club_colors} />
      </div>
      <div className="w-16 text-center shrink-0">
        {isResult ? (
          <span className="font-mono font-bold text-white">{match.home_score} - {match.away_score}</span>
        ) : (
          <span className="text-[10px] text-slate-500 uppercase">{dateStr}</span>
        )}
      </div>
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <ClubBadge name={match.away_club} logo={awayLogo} colors={match.away_club_colors} />
        <span className="text-sm font-medium text-white truncate">{match.away_club}</span>
      </div>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Landing = () => {
  const { t, i18n } = useTranslation();
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [recentResults, setRecentResults] = useState([]);
  const isItalian = i18n.language === 'it';
  const lang = i18n.language;
  const tx = (it, en, de) => lang === 'it' ? it : lang === 'de' ? de : en;

  // PWA opens at start_url='/' (Landing). If the user is already logged in
  // and has a previously visited route, send them straight there — this is
  // critical on mobile where Chrome may have killed the tab while the user
  // was on /tornei-partite, /admin, etc. Compute redirect synchronously
  // (before any hooks-after-conditional issue) — note: this is read once
  // per mount which is fine.
  const _hasToken = (() => { try { return !!localStorage.getItem('token'); } catch { return false; } })();
  const _lastRoute = (() => { try { return localStorage.getItem('lastRoute'); } catch { return null; } })();
  const _redirectTo = _hasToken
    ? ((_lastRoute && _lastRoute !== '/' && _lastRoute !== '/login') ? _lastRoute : '/dashboard')
    : null;

  useEffect(() => {
    fetch(`${API_URL}/api/stats/upcoming-matches?limit=5`).then(r => r.json()).then(setUpcomingMatches).catch(() => {});
    fetch(`${API_URL}/api/stats/recent-results?limit=5`).then(r => r.json()).then(setRecentResults).catch(() => {});
  }, []);

  const isIOS = typeof navigator !== 'undefined' && (/iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1));
  const isStandalone = typeof window !== 'undefined' && (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);

  const handleInstallClick = useCallback(() => {
    if (window.__pwaInstall?.canInstall()) {
      window.__pwaInstall.trigger();
    } else if (isIOS) {
      setShowIOSGuide(true);
    } else {
      // Fallback: show browser install instructions
      setShowIOSGuide(true);
    }
  }, [isIOS]);

  // Redirect logged-in users (PWA cold start re-opens at '/').
  if (_redirectTo) return <Navigate to={_redirectTo} replace />;

  const features = [
    {
      icon: Shield,
      title: t('landing.features.clubs'),
      desc: t('landing.features.clubsDesc'),
      color: '#00F0FF',
    },
    {
      icon: BarChart3,
      title: t('landing.features.matches'),
      desc: t('landing.features.matchesDesc'),
      color: '#00FF87',
    },
    {
      icon: Trophy,
      title: t('landing.features.stats'),
      desc: t('landing.features.statsDesc'),
      color: '#FFE600',
    },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden ept-bg-ambient">
      {/* Grid background */}
      <div className="absolute inset-0 grid-bg pointer-events-none" />

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#050508]/90 backdrop-blur-xl border-b border-[#00F0FF]/10" style={{boxShadow: '0 1px 30px rgba(0,240,255,0.04)'}}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <img src="/ept-logo.png" alt="EPT Italy" className="h-9 w-auto object-contain" style={{filter: 'drop-shadow(0 0 8px rgba(0,240,255,0.3))'}} />
              <span className="font-heading font-bold text-base uppercase tracking-wider neon-text-cyan">EPT ITALY</span>
            </div>
            {/* Desktop nav */}
            <div className="hidden sm:flex items-center gap-2">
              <Link to="/classifica">
                <Button variant="ghost" size="sm" className="text-[#FFEA00] hover:text-[#FFEA00]/80 text-xs uppercase tracking-wider h-8" data-testid="header-classifica">
                  <Trophy size={14} className="mr-1" />
                  {tx('Classifica', 'Standings', 'Tabelle')}
                </Button>
              </Link>
              <Link to="/tornei">
                <Button variant="ghost" size="sm" className="text-[#00F0FF] hover:text-[#00F0FF]/80 text-xs uppercase tracking-wider h-8" data-testid="header-tornei">
                  <Shield size={14} className="mr-1" />
                  {tx('Tornei', 'Tournaments', 'Turniere')}
                </Button>
              </Link>
              <Link to="/regolamento">
                <Button variant="ghost" size="sm" className="text-[#00E676] hover:text-[#00E676]/80 text-xs uppercase tracking-wider h-8" data-testid="header-regolamento">
                  <BookOpen size={14} className="mr-1" />
                  {tx('Regolamento', 'Rules', 'Regeln')}
                </Button>
              </Link>
            </div>
            {/* Mobile nav */}
            <div className="flex sm:hidden items-center gap-1">
              <Link to="/classifica">
                <Button variant="ghost" size="icon" className="text-[#FFEA00] hover:text-[#FFEA00]/80 h-9 w-9" data-testid="header-classifica-mobile">
                  <Trophy size={18} />
                </Button>
              </Link>
              <Link to="/tornei">
                <Button variant="ghost" size="icon" className="text-[#00F0FF] hover:text-[#00F0FF]/80 h-9 w-9" data-testid="header-tornei-mobile">
                  <Shield size={18} />
                </Button>
              </Link>
              <Link to="/regolamento">
                <Button variant="ghost" size="icon" className="text-[#00E676] hover:text-[#00E676]/80 h-9 w-9" data-testid="header-regolamento-mobile">
                  <BookOpen size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-20 sm:pt-32 pb-12 sm:pb-20 relative">
        {/* Neon glows */}
        <div className="absolute top-1/3 left-1/4 w-[500px] h-[500px] bg-[#00F0FF]/8 rounded-full blur-[150px] pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#FF00E5]/6 rounded-full blur-[130px] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Text */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
            >
              <motion.div variants={{ hidden: { opacity: 0, x: -30, filter: 'blur(8px)' }, visible: { opacity: 1, x: 0, filter: 'blur(0px)', transition: { duration: 0.5 } } }} className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#2979FF]/10 border border-[#2979FF]/30 rounded-sm mb-4 sm:mb-6">
                <Zap size={14} className="text-[#2979FF]" />
                <span className="text-xs sm:text-sm text-[#2979FF] font-medium uppercase tracking-wider">
                  {tx('Gestione Campionati eSports', 'eSports Championship Manager', 'eSport-Meisterschaftsverwaltung')}
                </span>
              </motion.div>
              
              <motion.h1 variants={{ hidden: { opacity: 0, y: 30, filter: 'blur(10px)' }, visible: { opacity: 1, y: 0, filter: 'blur(0px)', transition: { duration: 0.6 } } }} className="font-heading text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white mb-4 sm:mb-6 leading-none">
                <span className="neon-text-cyan">{t('landing.title')}</span>
              </motion.h1>
              
              <motion.p variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }} className="text-sm sm:text-base md:text-lg text-slate-400 mb-6 sm:mb-8 max-w-lg">
                {t('landing.subtitle')}
              </motion.p>
              
              <motion.div variants={{ hidden: { opacity: 0, y: 20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } }} className="flex flex-wrap gap-2 sm:gap-3">
                <Link to="/register">
                  <Button className="btn-primary px-5 sm:px-8 py-3 sm:py-5 text-sm sm:text-base neon-glow-hover" data-testid="cta-get-started">
                    {t('landing.cta')}
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="px-5 sm:px-8 py-3 sm:py-5 text-sm sm:text-base" data-testid="cta-login">
                    {t('nav.login')}
                  </Button>
                </Link>
              </motion.div>
              
              <motion.div variants={{ hidden: { opacity: 0, y: 15 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } }} className="flex flex-wrap gap-2 mt-3">
                <Link to="/classifica">
                  <Button variant="outline" size="sm" className="px-4 py-2 text-xs border-[#FFEA00]/30 text-[#FFEA00] hover:bg-[#FFEA00]/10" data-testid="cta-classifica">
                    <Trophy size={14} className="mr-1.5" />
                    {tx('Classifica Live', 'Live Standings', 'Live-Tabelle')}
                  </Button>
                </Link>
                {!isStandalone && (
                  <Button
                    data-testid="landing-install-btn"
                    onClick={handleInstallClick}
                    size="sm"
                    className="px-4 py-2 text-xs bg-[#00FF87] hover:bg-[#00FF87]/90 text-[#050508] font-bold"
                    style={{boxShadow: '0 0 10px rgba(0,255,135,0.2)'}}
                  >
                    <Download size={14} className="mr-1.5" />
                    Scarica App
                  </Button>
                )}
              </motion.div>

              {/* Stats */}
              <motion.div variants={{ hidden: { opacity: 0, y: 25 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5 } } }} className="flex gap-6 sm:gap-8 mt-8 sm:mt-12 pt-6 sm:pt-8 border-t border-[#00F0FF]/15">
                {[
                  { value: '1M+', label: tx('Budget Monete', 'Coin Budget', 'M\u00fcnzen-Budget'), cls: 'neon-text-cyan', style: {} },
                  { value: 'Live', label: tx('Report Partite', 'Match Reports', 'Spielberichte'), cls: 'text-[#00FF87]', style: { textShadow: '0 0 8px rgba(0,255,135,0.5)' } },
                  { value: '24/7', label: tx('Trasferimenti', 'Transfers', 'Transfers'), cls: 'neon-text-magenta', style: {} },
                ].map((stat, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7 + i * 0.15, type: 'spring', stiffness: 200, damping: 12 }}
                  >
                    <p className={`font-mono text-2xl sm:text-3xl font-bold ${stat.cls}`} style={stat.style}>{stat.value}</p>
                    <p className="text-xs sm:text-sm text-slate-500 uppercase tracking-wider">{stat.label}</p>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>

            {/* Right - Hero Image */}
            <motion.div
              initial={{ opacity: 0, scale: 0.85, rotate: 2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.8, delay: 0.3, type: 'spring', stiffness: 80 }}
              className="relative hidden md:block"
            >
              <div className="relative overflow-hidden border border-[#00F0FF]/15 neon-glow">
                <img
                  src="https://images.unsplash.com/photo-1758410473735-c76baff30a79?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwxfHxlc3BvcnRzJTIwZ2FtZXIlMjB0b3VybmFtZW50JTIwYXRtb3NwaGVyZSUyMGRhcmslMjBuZW9ufGVufDB8fHx8MTc3Mjk5MTkzNHww&ixlib=rb-4.1.0&q=85"
                  alt="eSports Tournament"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050508] via-transparent to-transparent" />
              </div>
              
              {/* Floating card */}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
            whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight mb-4">
              <span className="neon-text-cyan">{tx('Tutto Ci\u00f2 di Cui Hai Bisogno', 'Everything You Need', 'Alles was du brauchst')}</span>
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              {tx(
                'Sistema completo di gestione campionati con tutti gli strumenti per gestire la tua lega',
                'Complete championship management system with all the tools to manage your league',
                'Komplettes Meisterschaftsverwaltungssystem mit allen Werkzeugen f\u00fcr deine Liga'
              )}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 40, scale: 0.92 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true, margin: '-30px' }}
                transition={{ delay: index * 0.15, duration: 0.5, type: 'spring', stiffness: 100, damping: 15 }}
                whileHover={{ y: -6, transition: { duration: 0.2 } }}
                className="group bg-[#0a0d15] border border-white/5 p-6 relative overflow-hidden"
                style={{ willChange: 'transform' }}
              >
                <div className="absolute -top-10 -right-10 w-20 h-20 rounded-full blur-[40px] pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ backgroundColor: `${feature.color}30` }} />
                <motion.div
                  initial={{ scale: 0, rotate: -45 }}
                  whileInView={{ scale: 1, rotate: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 + index * 0.15, type: 'spring', stiffness: 200, damping: 12 }}
                  className="w-11 h-11 flex items-center justify-center mb-4 border border-white/5"
                  style={{ backgroundColor: `${feature.color}10` }}
                >
                  <feature.icon size={22} style={{ color: feature.color }} />
                </motion.div>
                <h3 className="font-heading text-base font-semibold text-white uppercase tracking-wider mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-slate-500">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Matches Section */}
      {(upcomingMatches.length > 0 || recentResults.length > 0) && (
        <section className="py-16 relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30, filter: 'blur(6px)' }}
              whileInView={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.5 }}
              className="text-center mb-10"
            >
              <h2 className="font-heading text-3xl md:text-4xl font-bold uppercase tracking-tight mb-2">
                <span className="neon-text-cyan">{tx('Partite', 'Matches', 'Spiele')}</span>
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-6">
              {/* Upcoming */}
              {upcomingMatches.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: -40, rotate: -1 }}
                  whileInView={{ opacity: 1, x: 0, rotate: 0 }}
                  viewport={{ once: true, margin: '-30px' }}
                  transition={{ duration: 0.6, type: 'spring', stiffness: 80 }}
                  className="bg-[#0a0d15] border border-white/5 rounded overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                    <Zap size={14} className="text-[#FFEA00]" />
                    <span className="text-xs uppercase tracking-wider text-[#FFEA00] font-bold">{tx('Prossime Partite', 'Upcoming', 'N\u00e4chste Spiele')}</span>
                  </div>
                  <div className="divide-y divide-white/5">
                    {upcomingMatches.map((m, i) => (
                      <MatchCard key={i} match={m} isResult={false} />
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Recent Results */}
              {recentResults.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, x: 40, rotate: 1 }}
                  whileInView={{ opacity: 1, x: 0, rotate: 0 }}
                  viewport={{ once: true, margin: '-30px' }}
                  transition={{ duration: 0.6, delay: 0.1, type: 'spring', stiffness: 80 }}
                  className="bg-[#0a0d15] border border-white/5 rounded overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
                    <Trophy size={14} className="text-[#00E676]" />
                    <span className="text-xs uppercase tracking-wider text-[#00E676] font-bold">{tx('Risultati Recenti', 'Recent Results', 'Letzte Ergebnisse')}</span>
                  </div>
                  <div className="divide-y divide-white/5">
                    {recentResults.map((m, i) => (
                      <MatchCard key={i} match={m} isResult={true} />
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.7, type: 'spring', stiffness: 70, damping: 14 }}
            className="bg-[#0a0d15] border border-[#00F0FF]/10 p-8 md:p-12 text-center relative overflow-hidden scanline-overlay"
          >
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[400px] h-[200px] bg-[#00F0FF]/10 rounded-full blur-[80px] pointer-events-none" />
            <div className="absolute bottom-0 right-1/4 w-[300px] h-[150px] bg-[#FF00E5]/8 rounded-full blur-[60px] pointer-events-none" />
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="font-heading text-2xl md:text-4xl font-bold uppercase tracking-tight mb-4 relative z-10"
            >
              <span className="neon-text-cyan">{tx('Pronto per Iniziare?', 'Ready to Start?', 'Bereit loszulegen?')}</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.35, duration: 0.5 }}
              className="text-slate-400 mb-8 max-w-lg mx-auto relative z-10"
            >
              {tx(
                'Crea il tuo club, costruisci la tua rosa e competi nei campionati con capitani da tutto il mondo.',
                'Create your club, build your roster, and compete in championships with captains from around the world.',
                'Erstelle deinen Verein, baue deinen Kader auf und tritt in Meisterschaften gegen Kapit\u00e4ne aus aller Welt an.'
              )}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.45, type: 'spring', stiffness: 150, damping: 12 }}
            >
              <Link to="/register">
                <Button className="btn-primary px-8 py-5 text-base neon-glow-hover relative z-10" data-testid="final-cta">
                  {t('landing.cta')}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="py-8 border-t border-[#00F0FF]/10"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-[#00F0FF]" />
              <span className="font-heading font-bold uppercase tracking-wider neon-text-cyan text-sm">
                EPT ITALY
              </span>
            </div>
            <div className="flex items-center gap-4">
              {!isStandalone && (
                <button
                  data-testid="footer-install-btn"
                  onClick={handleInstallClick}
                  className="flex items-center gap-2 text-sm text-[#00FF87] hover:text-[#00FF87]/80 transition-colors"
                  style={{textShadow: '0 0 6px rgba(0,255,135,0.3)'}}
                >
                  <Download size={16} />
                  Scarica App
                </button>
              )}
              <p className="text-sm text-slate-500">
                Powered By EPT ITALY 2026
              </p>
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-[#00F0FF] transition-colors" data-testid="footer-privacy-link">
                Privacy &amp; Cookie
              </Link>
            </div>
          </div>
        </div>
      </motion.footer>

      {/* iOS/Generic Install Guide Modal */}
      {showIOSGuide && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm" onClick={() => setShowIOSGuide(false)}>
          <div className="bg-[#151921] border border-white/10 rounded-t-3xl sm:rounded-3xl p-6 w-full max-w-md animate-in slide-in-from-bottom duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="w-10 h-1 bg-gray-600 rounded-full mx-auto mb-5 sm:hidden" />
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-bold text-lg flex items-center gap-2">
                <Smartphone size={20} className="text-[#2563eb]" />
                {isIOS ? 'Installa EPT Italy su iOS' : 'Installa EPT Italy'}
              </h3>
              <button onClick={() => setShowIOSGuide(false)} className="text-gray-500 hover:text-white"><X size={20} /></button>
            </div>
            {isIOS ? (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">1</span>
                  <p className="text-gray-300 text-sm">Tocca <Share2 size={14} className="inline text-[#2563eb]" /> <strong className="text-white">Condividi</strong> nella barra di Safari</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">2</span>
                  <p className="text-gray-300 text-sm">Tocca <strong className="text-white">"Aggiungi alla schermata Home"</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">3</span>
                  <p className="text-gray-300 text-sm">Tocca <strong className="text-white">"Aggiungi"</strong> in alto a destra</p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">1</span>
                  <p className="text-gray-300 text-sm">Su <strong className="text-white">Chrome</strong>: clicca l'icona di installazione nella barra degli indirizzi, oppure apri il menu (tre puntini) e seleziona <strong className="text-white">"Installa app"</strong></p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#2563eb]/20 text-[#2563eb] font-bold text-sm flex items-center justify-center">2</span>
                  <p className="text-gray-300 text-sm">Su <strong className="text-white">Android</strong>: tocca <strong className="text-white">"Aggiungi a schermata Home"</strong> dal menu del browser</p>
                </div>
              </div>
            )}
            <button onClick={() => setShowIOSGuide(false)} className="mt-6 w-full py-3 bg-white/10 hover:bg-white/15 text-white text-sm font-semibold rounded-xl transition-colors">Ho capito</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
